import z from "zod/v4";

import { CheckoutApplicationFieldType } from "@/models/options";

import type { IOffer } from "@/models/funders";

const checkoutStepTypeSchema = z.enum([
  "documents",
  "consents",
  "contracts",
  "data",
]);

const checkoutDataFieldSchema = z.object({
  id: z.string(),
  type: z.enum(["application_field", "custom_field"]),
  name: z.string(),
  label: z.string(),
  value_type: z.enum(CheckoutApplicationFieldType).optional(),
  block: z.string().optional(),
});

const checkoutDataTableSchema = z.object({
  id: z.string(),
  type: z.enum(["table"]),
  layout: z.enum(["table", "form"]),
  fields: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      label: z.string(),
    })
  ),
  block: z.string(),
  title: z.string().optional(),
});

const checkoutDataStepItemSchema = z.discriminatedUnion("type", [
  checkoutDataFieldSchema,
  checkoutDataTableSchema,
]);

const checkoutTemplateStepSchema = z.object({
  id: z.string(),
  type: checkoutStepTypeSchema,
  name: z.string(),
  description: z.string().nullable(),

  /**
   * For consent and contract steps
   */
  templates: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]),
        owners_required: z.string().optional(),
      })
    )
    .optional(),

  /**
   * For data collection step
   */
  items: z.array(checkoutDataStepItemSchema).optional(),
});

export const checkoutTemplateBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  client: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable()
    .optional(),
  data_step_values: z.record(z.string(), z.unknown()).optional(),
});

const checkoutTemplateContentSchema = z.object({
  id: z.string().optional(),
  version: z.string().optional(),
  title: z.string(),
  subtitle: z.string().nullable(),
  checkout_section_name: z.string().nullable().optional(),
  offer_summary_section_name: z.string().nullable().optional(),
  should_mask_piis: z.boolean().optional(),
  steps: z.array(checkoutTemplateStepSchema),
  finalize: z.object({
    title: z.string().nullable(),
    description: z.string().nullable(),
  }),
});

export const checkoutTemplateSchema = checkoutTemplateBaseSchema.extend({
  content: checkoutTemplateContentSchema,
});

export type CheckoutTemplateBase = z.infer<typeof checkoutTemplateBaseSchema>;
export type CheckoutTemplateContent = z.infer<
  typeof checkoutTemplateContentSchema
>;
export type CheckoutTemplate = z.infer<typeof checkoutTemplateSchema>;

export type CheckoutStep = z.infer<typeof checkoutTemplateStepSchema>;
export type CheckoutStepType = z.infer<typeof checkoutStepTypeSchema>;

export type CheckoutDataField = z.infer<typeof checkoutDataFieldSchema>;
export type CheckoutDataTable = z.infer<typeof checkoutDataTableSchema>;
export type CheckoutDataStepItem = z.infer<typeof checkoutDataStepItemSchema>;

export type CheckoutDocumentsStep = CheckoutStep;

export type CheckoutConsentsStep = CheckoutStep &
  Required<Pick<CheckoutStep, "templates">>;

export type CheckoutContractsStep = CheckoutStep &
  Required<Pick<CheckoutStep, "templates">>;

export type CheckoutDataStep = CheckoutStep &
  Required<Pick<CheckoutStep, "items">>;

export type CheckoutSnapshot = Omit<CheckoutTemplate, "id"> & {
  id: number;
  offer_id: IOffer["id"];
  checkout_template_id: NonNullable<CheckoutTemplateBase["id"]>;
};

export type UpdateCheckoutSnapshotParams = {
  offerId: string;
  checkoutSnapshotId: string;
  checkoutContent: CheckoutTemplateContent;
};

export type CheckoutBuilderMode = "in-progress" | "complete";

export type CheckoutProgress = {
  id: number;
  checkout_snapshot_id: number;
  status: "complete" | "incomplete";
  step_statuses: {
    id: string;
    is_complete: boolean;
  }[];
  started_at: string;
  completed_at: string;
  created_at: string;
  updated_at: string;
};
